import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  TextField,
  MenuItem,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";

import PersonAddIcon from "@mui/icons-material/PersonAdd";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutlineOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";

import PageContainer from "../layouts/PageContainer";
import PageToolbar from "../components/common/PageToolbar";
import AppButton from "../components/buttons/AppButton";
import StandardTable, { TableColumn } from "../components/common/StandardTable";
import StatusChip from "../components/common/StatusChip";
import ConfirmDialog from "../components/common/ConfirmDialog";
import CreateUserModal from "../components/users/CreateUserModal";
import UpdateUserModal from "../components/users/UpdateUserModal";
import ViewUserModal from "../components/users/ViewUserModal";

import useDebounce from "../hooks/useDebounce";
import { usePermissions } from "../hooks/usePermissions";
import { useAppDispatch, useAppSelector } from "../redux/store";
import {
  getUsers,
  getUserById,
  deleteUser,
} from "../redux/actions/userActions";
import { getRoles } from "../redux/actions/roleActions";
import { userActions } from "../redux/slices/user";

import { castEnumForDisplay } from "../helpers/enumHelpers";
import { UserSummaryDto } from "../shared/dtos/user/UserSummaryDto";
import { UserStatus } from "../shared/enums/UserStatus";

const UsersPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const { hasPermission } = usePermissions();

  // --- Redux State ---
  const {
    items: users,
    loadingFetchAll: loadingUsers,
    errorFetchAll: errorLoadingUsers,
    pagination,
    serverStatus,
    loadingCreate: loadingUserCreate,
    loadingUpdate: loadingUserUpdate,
    loadingDelete: loadingUserDelete,
    item: user,
    loadingFetchOne: loadingUser,
    errorFetchOne: errorLoadingUser,
    errorCreate: errorCreatingUser,
    errorUpdate: errorUpdatingUser,
  } = useAppSelector((state) => state.user);
  const { items: roles } = useAppSelector((state) => state.role);
  const { userInfo } = useAppSelector((state) => state.auth);

  // --- Local State ---
  const [searchTerm, setSearchTerm] = useState<string>("");
  const debouncedSearch = useDebounce<string>(searchTerm, 500);

  const [filters, setFilters] = useState({ role: "", status: "" });
  const [pageConfig, setPageConfig] = useState({ page: 1, limit: 10 });

  // Modal State
  const [isCreateModalOpen, setIsCreateModalOpen] = useState<boolean>(false);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState<boolean>(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState<boolean>(false);
  const [userForOperation, setUserForOperation] =
    useState<UserSummaryDto | null>(null); // User for edit/delete

  // --- Permissions ---
  const canCreateUser = hasPermission("user:create");
  const canEditUser = hasPermission("user:update");
  const canDeleteUser = hasPermission("user:delete");
  const canViewUser = hasPermission("user:read");

  // --- Data Fetching ---
  useEffect(() => {
    if (!roles || roles.length === 0) {
      dispatch(getRoles());
    }
  }, [dispatch, roles]);

  const fetchUsers = useCallback(() => {
    dispatch(
      getUsers({
        page: pageConfig.page,
        size: pageConfig.limit,
        search: debouncedSearch,
        roleName: filters.role,
        status: (filters.status as UserStatus) || undefined,
      }),
    );
  }, [
    dispatch,
    pageConfig.page,
    pageConfig.limit,
    debouncedSearch,
    filters.role,
    filters.status,
  ]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const roleOptions = useMemo(
    () =>
      roles?.map((r) => ({
        value: r.name,
        label: castEnumForDisplay(r.name),
      })) || [],
    [roles],
  );

  // --- Modal Handlers ---
  const handleOpenCreate = (): void => setIsCreateModalOpen(true);
  const handleCloseCreate = useCallback(() => {
    if (!loadingUserCreate) setIsCreateModalOpen(false);
  }, [loadingUserCreate]);

  const handleOpenUpdate = (u: UserSummaryDto): void => {
    setUserForOperation(u);
    setIsUpdateModalOpen(true);
  };
  const handleCloseUpdate = useCallback(() => {
    if (!loadingUserUpdate) {
      setIsUpdateModalOpen(false);
      setUserForOperation(null);
    }
  }, [loadingUserUpdate]);

  const handleOpenDelete = (u: UserSummaryDto): void => {
    setUserForOperation(u);
    setIsDeleteModalOpen(true);
  };
  const handleCloseDelete = useCallback(() => {
    if (!loadingUserDelete) {
      setIsDeleteModalOpen(false);
      setUserForOperation(null);
    }
  }, [loadingUserDelete]);

  const handleOpenView = (u: UserSummaryDto): void => {
    dispatch(getUserById(u.id));
    setIsViewModalOpen(true);
  };
  const handleCloseView = (): void => setIsViewModalOpen(false);

  const handleDeleteConfirm = (): void => {
    if (userForOperation) {
      dispatch(deleteUser(userForOperation.id));
    }
  };

  // Auto-close modals on success
  useEffect(() => {
    if (!serverStatus) return;
    const { operation, status } = serverStatus;

    if (operation === "create" && status === 201) handleCloseCreate();
    if (operation === "update" && status === 200) handleCloseUpdate();
    if (operation === "delete" && status === 200) handleCloseDelete();

    dispatch(userActions.setServerStatus(null));
  }, [
    serverStatus,
    dispatch,
    handleCloseCreate,
    handleCloseUpdate,
    handleCloseDelete,
  ]);

  // --- Table Configuration ---
  const columns: TableColumn<UserSummaryDto>[] = useMemo(
    () => [
      {
        id: "firstName",
        label: "Full Name",
        minWidth: 170,
        format: (_, row) => (
          <Typography component="span" sx={{ fontWeight: 600 }}>
            {row.firstName} {row.lastName}
          </Typography>
        ),
      },
      { id: "username", label: "Username", minWidth: 200 },
      { id: "email", label: "Email Address", minWidth: 200 },
      {
        id: "userRole",
        label: "Role",
        minWidth: 120,
        format: (_, row) => castEnumForDisplay(row.userRole),
      },
      {
        id: "status",
        label: "Status",
        minWidth: 120,
        format: (_, row) => <StatusChip status={row.status} />,
      },
      {
        id: "createdAt",
        label: "Joined",
        minWidth: 120,
        format: (_, row) => new Date(row.createdAt).toLocaleDateString(),
      },
    ],
    [],
  );

  const renderRowActions = (row: UserSummaryDto): React.JSX.Element => (
    <Stack direction="row" spacing={1} sx={{ justifyContent: "center" }}>
      {canViewUser && (
        <Tooltip title="View Details">
          <IconButton
            size="small"
            color="info"
            onClick={() => handleOpenView(row)}
          >
            <VisibilityOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
      {canEditUser && (
        <Tooltip title="Edit User">
          <IconButton
            size="small"
            color="secondary"
            onClick={() => handleOpenUpdate(row)}
          >
            <EditOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
      {canDeleteUser && (
        <Tooltip title="Delete User">
          <IconButton
            size="small"
            color="error"
            onClick={() => handleOpenDelete(row)}
          >
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </Stack>
  );

  return (
    <PageContainer
      title="User Management"
      subtitle="Manage system access, roles, and administrative accounts."
      actions={
        canCreateUser && (
          <AppButton
            variant="contained"
            startIcon={<PersonAddIcon />}
            onClick={handleOpenCreate}
          >
            Create New User
          </AppButton>
        )
      }
    >
      <PageToolbar
        searchTerm={searchTerm}
        onSearchChange={(e) => setSearchTerm(e.target.value)}
        searchPlaceholder="Search users..."
      >
        <TextField
          select
          label="Role"
          size="small"
          value={filters.role}
          onChange={(e) => setFilters({ ...filters, role: e.target.value })}
          sx={{ minWidth: 150 }}
        >
          <MenuItem value="">All Roles</MenuItem>
          {roleOptions.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          label="Status"
          size="small"
          value={filters.status}
          onChange={(e) => setFilters({ ...filters, status: e.target.value })}
          sx={{ minWidth: 150 }}
        >
          <MenuItem value="">All Status</MenuItem>
          <MenuItem value="ACTIVE">Active</MenuItem>
          <MenuItem value="INACTIVE">Inactive</MenuItem>
          <MenuItem value="PENDING_VERIFICATION">Pending Verification</MenuItem>
        </TextField>
      </PageToolbar>

      <StandardTable
        columns={columns}
        data={users}
        loading={loadingUsers}
        error={errorLoadingUsers}
        onRetry={fetchUsers}
        page={pageConfig.page}
        count={pagination?.totalElements || 0}
        rowsPerPage={pageConfig.limit}
        onPageChange={(newPage) =>
          setPageConfig({ ...pageConfig, page: newPage })
        }
        onRowsPerPageChange={(newLimit) =>
          setPageConfig({ ...pageConfig, limit: newLimit, page: 1 })
        }
        rowAction={renderRowActions}
      />

      {/* --- MODALS --- */}
      {isCreateModalOpen && userInfo && (
        <CreateUserModal
          open={isCreateModalOpen}
          onClose={handleCloseCreate}
          loading={loadingUserCreate}
          error={errorCreatingUser}
        />
      )}
      {isUpdateModalOpen && userForOperation && userInfo && (
        <UpdateUserModal
          open={isUpdateModalOpen}
          onClose={handleCloseUpdate}
          user={userForOperation}
          loading={loadingUserUpdate}
          error={errorUpdatingUser}
        />
      )}
      {isViewModalOpen && (
        <ViewUserModal
          open={isViewModalOpen}
          onClose={handleCloseView}
          user={user}
          loading={loadingUser}
          error={errorLoadingUser}
        />
      )}
      {isDeleteModalOpen && userForOperation && (
        <ConfirmDialog
          open={isDeleteModalOpen}
          onClose={handleCloseDelete}
          onConfirm={handleDeleteConfirm}
          title="Delete User"
          description={`Are you sure you want to delete the user "${userForOperation.firstName} ${userForOperation.lastName}"? This action cannot be undone.`}
          loading={loadingUserDelete}
        />
      )}
    </PageContainer>
  );
};

export default UsersPage;
